import AppLoader from "../components/AppLoader";
import { useEffect, useState, useRef } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  Alert,
  Fade,
  FormControl,
  InputLabel,
  Select,
  useTheme,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/AuthContext";
// import { api } from "../services/api";
import { useLocation } from "react-router-dom";
import AppBackground from "../components/layout/AppBackground";


const ContactForm = () => {
  const { t } = useTranslation("contact");
  const theme = useTheme();

  const { token } = useAuth();
  const { user } = useAuth();
  const nameRef = useRef(null);
  const messageRef = useRef(null);
  const [formLoading, setFormLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const typeParam = queryParams.get("type");
  const showCarpinchiaOption = user?.source === "undelta" || typeParam === "carpinchia";
    
  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
    type: typeParam || "",
  });

  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const typeRef = useRef(null);

  useEffect(() => {
    const initForm = () => {
      setForm({
        name: user?.first_name || "",
        email: user?.email || "",
        message: "",
        type: typeParam || "",
      });

      setFormLoading(false);

      setTimeout(() => {
        if (!typeParam) {
          typeRef.current?.focus();
        } else if (user) {
          messageRef.current?.focus();
        } else {
          nameRef.current?.focus();
        }
      }, 100);
    };

    if (user || !token) {
      initForm();
    }
  }, [user, token]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const API_URL = process.env.REACT_APP_API_URL || "";

  const handleSubmit = async (e) => {

    // 🌍 Log para verificar la URL de la API
    console.log("🌍 API_URL:", API_URL);
    // Verifica si la URL de la API está definida
    console.log("📡 Enviando a:", `${API_URL}/api/send-email`);

    e.preventDefault();
    setError("");
    setSent(false);
    setLoading(true); // ⏳ bloqueamos

    // Validación rápida de campos requeridos
    if (!form.name || !form.email || !form.type || !form.message) {
      setError(t("error_required_fields"));
      setLoading(false);
      return;
    }

    const isEarlyPitcher = user?.is_early_pitcher;
    // const finalMessage = isEarlyPitcher && form.type === "early_pitcher"
    const finalMessage = isEarlyPitcher 
      ? `[Early Pitcher] ${form.message}`
      : form.message;

    try {
      const response = await fetch(`${API_URL}/api/send-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          message: finalMessage, //form.message
          type: form.type,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || t("error_sending"));
      }

      // ✅ Éxito
      setSent(true);
      setForm({ name: "", email: "", type: "", message: "" });
    } catch (err) {
      console.error("❌ Error al enviar mensaje:", err);
      setError(err.message || t("error_sending"));
    } finally {
      setLoading(false); // 🔓 siempre desbloquear
    }
  };

  if (formLoading) return <AppLoader message={t("loading")} />;

  return (
    <Fade in timeout={700}>
      <AppBackground
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            backgroundColor: "#212121",
            color: "#fff",
            borderRadius: 4,
            p: 4,
            maxWidth: 600,
            width: "100%",
            textAlign: "center",
            boxShadow: "0 0 20px rgba(0,0,0,0.5)",
          }}
        >
          <img
            src="/assets/logo-icon.png"
            alt="Pitchest Logo"
            style={{ height: 80, marginBottom: 16 }}
          />
            <Typography variant="h5" gutterBottom sx={{ mt: 2, fontWeight: 600 }}>
              💬{t("contact_title")}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: "#ccc" }}>
              {t("contact_subtitle")}
            </Typography>
          {sent && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t("message_sent")}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* 🔽 Agrupamos los inputs y botón */}
          <Box sx={{ maxWidth: 400, mx: "auto" }}>
            <TextField
              label={t("label_name")}
              name="name"
              inputRef={nameRef}
              value={form.name}
              onChange={handleChange}
              fullWidth
              margin="normal"
              size="small"
              inputProps={{ maxLength: 50 }}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#bbb", // texto del input
                },
                "& .MuiInputLabel-root.Mui-disabled": {
                  color: "#bbb", // label
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#bbb", // opcional: borde gris claro
                },
              }}
            />
            <TextField
              label={t("label_email")}
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              fullWidth
              margin="normal"
              size="small"
              inputProps={{ maxLength: 50 }}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#bbb", // texto del input
                },
                "& .MuiInputLabel-root.Mui-disabled": {
                  color: "#bbb", // label
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#bbb", // opcional: borde gris claro
                },
              }}
            />
            <FormControl fullWidth margin="normal" size="small">
              <InputLabel
                id="type-label"
                shrink
                sx={{
                  color: "#eee",
                  mb: 0.5, // 👈 subimos el label para evitar solapamiento
                }}
              >
                {t("label_type")}
              </InputLabel>
              <Select
                inputRef={typeRef}
                labelId="type-label"
                id="type"
                name="type"
                value={form.type}
                onChange={handleChange}
                label={t("label_type")}
                disabled={loading}
                displayEmpty
                sx={{
                  color: "#fff",
                  ".MuiSelect-icon": { color: "#454346" },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#454346" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#A855F7" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#A855F7",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "#fff", // fondo del menú desplegable
                      color: "#222", // color del texto
                      "& .MuiMenuItem-root": {
                        fontWeight: 500,
                      },
                      "& .Mui-selected": {
                        backgroundColor: "#eee",
                        color: "#555",
                      },
                      "& .Mui-selected:hover": {
                        backgroundColor: "#ddd",
                      },
                    },
                  },
                }}
              >
                <MenuItem value="">{t("type_placeholder")}</MenuItem>
                <MenuItem value="feedback">{t("type_feedback")}</MenuItem>
                <MenuItem value="early_pitcher">{t("type_early_pitcher")}</MenuItem>
                {showCarpinchiaOption && (
                  <MenuItem value="carpinchia">{t("type_carpinchia")}</MenuItem>
                )}
                <MenuItem value="team_plan">{t("type_team_plan")}</MenuItem>
                <MenuItem value="billing">{t("type_billing")}</MenuItem>
                <MenuItem value="bug">{t("type_bug")}</MenuItem>
                <MenuItem value="technical">{t("type_technical")}</MenuItem>
                <MenuItem value="feature">{t("type_feature_request")}</MenuItem>
                <MenuItem value="other">{t("type_other")}</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label={t("label_message")}
              name="message"
              inputRef={messageRef}
              value={form.message}
              onChange={handleChange}
              fullWidth
              multiline
              rows={5}
              margin="normal"
              size="small"
              InputLabelProps={{ shrink: true }}
              disabled={loading}
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#bbb", // texto del input
                },
                "& .MuiInputLabel-root.Mui-disabled": {
                  color: "#bbb", // label
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#bbb", // opcional: borde gris claro
                },
              }}
            />

            <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                    mt: 2,
                    borderRadius: 999,
                    py: 1.2,
                    color: "#fff", // 🔥 fuerza el color del texto
                    "&.Mui-disabled": {
                    color: "#fff", // 🔒 mantiene color al estar deshabilitado
                    opacity: 0.7,  // opcional: da efecto de "bloqueado" sin hacerlo invisible
                    },
                }}
            >
                {loading ? t("sending") : t("send_button")}
            </Button>
          </Box>
        </Box>
      </AppBackground>
    </Fade>
  );
};

export default ContactForm;
